import React, {useEffect, useMemo, useRef, useState} from 'react'
import styles from './ReplaySlider.module.scss'
import {type ImageURL} from '@colonist/types'
import clsx from 'clsx'
import IconLongestRoad from '@static/images/icon_longest_road_highlight.svg'
import IconLargestArmy from '@static/images/icon_largest_army_highlight.svg'
import IconCardMonopoly from '@static/images/card_monopoly.svg'

export interface ReplayHighlightData {
    actionIndex: number
    type: ReplayHighlightType
}

export type ReplayHighlightType = 'largestArmy' | 'longestRoad' | 'monopolyPlayed'

const replayHighlightTypeToLobbyImage: Record<ReplayHighlightType, ImageURL> = {
    largestArmy: IconLargestArmy,
    longestRoad: IconLongestRoad,
    monopolyPlayed: IconCardMonopoly,
}

const GRID_AREA_OFFSET = 2

export const ReplaySlider = ({
    value,
    max,
    highlightData,
    onChange,
    shouldShowHighlights,
}: {
    value: number
    max: number
    shouldShowHighlights?: boolean
    highlightData?: ReplayHighlightData[]
    onChange: (value: number) => void
}) => {
    const [isDragging, setIsDragging] = useState(false)
    const valueRef = useRef(value)
    const sliderRef = useRef<HTMLDivElement>(null)

    const handleChange = (newValue: number) => {
        const isNewValueFirstAction = newValue <= -1
        const isNewValueLastAction = newValue >= max
        const isNewValueSameValue = newValue === valueRef.current

        if(isNewValueSameValue) return
        if(isNewValueFirstAction) onChange(-1)
        if(isNewValueLastAction) onChange(max)
        valueRef.current = newValue
        onChange(newValue)
    }

    const highlights = useMemo(() => {
        if(!highlightData) return null
        const sorted = [...highlightData].sort((a, b) => a.actionIndex - b.actionIndex)

        return sorted.map((highlight) => (
            <div
                onClick={() => onChange(highlight.actionIndex)}
                key={highlight.actionIndex}
                className={styles.replaySliderHighlight}
                style={{gridArea: `2 / ${highlight.actionIndex + GRID_AREA_OFFSET}`}}>
                <img
                    className={styles.replaySliderHighlightImage}
                    src={replayHighlightTypeToLobbyImage[highlight.type]}
                />
            </div>))
    }, [highlightData])

    const handlePointerDown = () => {
        setIsDragging(true)
    }

    useEffect(() => {
        const handlePointerUp = () => {
            setIsDragging(false)
        }

        const handlePointerMove = (e: MouseEvent | TouchEvent) => {
            if(!isDragging || !sliderRef.current) return
            const sliderRect = sliderRef.current.getBoundingClientRect()
            const sliderRailHalfHeight = 6
            const sliderMidY = sliderRect.top + sliderRailHalfHeight
            const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX
            const el = document.elementFromPoint(clientX, sliderMidY)
            const index = (el as HTMLElement)?.dataset?.index
            const isPointerExceedSliderLeftEdge = clientX <= sliderRect.left
            const defaultIndex = isPointerExceedSliderLeftEdge ? -1 : max
            handleChange(Number(index || defaultIndex))
        }

        if(isDragging) {
            window.addEventListener('mousemove', handlePointerMove)
            window.addEventListener('touchmove', handlePointerMove)
            window.addEventListener('mouseup', handlePointerUp)
            window.addEventListener('touchend', handlePointerUp)
        }

        return () => {
            window.removeEventListener('mousemove', handlePointerMove)
            window.removeEventListener('touchmove', handlePointerMove)
            window.removeEventListener('mouseup', handlePointerUp)
            window.removeEventListener('touchend', handlePointerUp)
        }
    }, [isDragging, max])

    const handleTrackWidth = useMemo(() => {
        if(value <= -1) return '0'
        if(value >= max) return '100%'
        return '50%'
    }, [value])

    return (
        <div className={clsx(styles.sliderContainer, {[styles.hightLightMargin]: shouldShowHighlights})} >
            <div
                ref={sliderRef}
                className={styles.sliderWrapper}
                style={{
                    '--total-actions': max + GRID_AREA_OFFSET,
                } as React.CSSProperties}>
                {Array.from({length: max + GRID_AREA_OFFSET}, (_, i) => (
                    <div
                        key={i}
                        className={styles.sliderGridClickableCell}
                        data-index={i - 1}
                        onPointerDown={() => {
                            setIsDragging(true)
                            handleChange(i - 1)
                        }}
                        style={{gridArea: `1 / ${i - 1 + GRID_AREA_OFFSET}`}}>
                    </div>
                ))}
                <div className={styles.sliderRail}></div>
                {value > -1 && <div className={clsx(styles.sliderTrack)} onMouseDown={handlePointerDown} onTouchStart={handlePointerDown} style={{
                    gridArea: `1 / ${value + GRID_AREA_OFFSET}`,
                    gridColumn: `1 / ${value + GRID_AREA_OFFSET}`,
                }}>
                </div>}
                <div className={clsx(styles.sliderTrackHandle)} onMouseDown={handlePointerDown} onTouchStart={handlePointerDown} style={{
                    width: handleTrackWidth,
                    gridArea: `1 / ${value + GRID_AREA_OFFSET}`,
                }}>
                    <div className={styles.sliderHandle} />
                </div>
                {highlights}
            </div>
        </div>
    )
}
